import { Group, Menu, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconBoxMultiple, IconOutbound } from '@tabler/icons-react';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { erroredOperationAtom } from '~/atoms/no-code-atoms';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useTestSuiteDialog } from '~/hooks/useTestSuiteDialog';
import { api } from '~/trpc-extra/api';
import { SplitButton } from '../common/SplitButton';

export default function PublishButton({ testContentId }: { testContentId: string }) {
  const trpcUtils = api.useUtils();
  const router = useRouter();
  const setErroredOperation = useSetAtom(erroredOperationAtom);

  const projectId = useProjectId();
  const { data: projectAdminInfo } = api.project.getProjectAdminInfo.useQuery(
    { projectId: projectId || '' },
    { enabled: !!projectId },
  );

  const extractTestKnowledgeMutation = api.knowledge.extractKnowledge.useMutation({
    onError: (error) => {
      console.error('Error extracting test knowledge:', error);
    },
  });

  const { data: testDraft } = api.testContent.get.useQuery(
    { id: testContentId },
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: !!testContentId },
  );

  const { mutate: publishTestDraft, isPending: isPublishing } = api.testDraft.publishTestDraft.useMutation({
    onSuccess: (publishedId) => {
      setErroredOperation(undefined);
      router.push(`/project/${testDraft?.test.projectId}/test/${publishedId.testId}`).catch(() => undefined);

      if (projectAdminInfo?.extractKnowledgeFromPublishedTests) {
        extractTestKnowledgeMutation.mutate({ testId: publishedId.testId });
      }
    },
    onSettled: () => {
      modals.closeAll();
    },
  });

  const { mutate: addToTestSuiteAndPublish } = api.testDraft.addToTestSuiteAndPublish.useMutation({
    onMutate: () => {
      notifications.show({
        title: 'Adding to test suite and publish',
        message: 'Please wait...',
      });
    },
    onSuccess: async () => {
      notifications.show({
        title: 'Added to test suite',
        message: 'Test has been added to test suite and published',
      });
      setErroredOperation(undefined);
      trpcUtils.testDraft.listByTestId.invalidate().catch(() => undefined);
      trpcUtils.testDraft.list.invalidate().catch(() => undefined);
      trpcUtils.test.getTest.invalidate().catch(() => undefined);
      trpcUtils.testContent.get.invalidate().catch(() => undefined);
      trpcUtils.testSuite.list.invalidate().catch(() => undefined);
      await router.push(`/project/${testDraft?.test.projectId}/test/${testDraft?.test.id}`);

      if (projectAdminInfo?.extractKnowledgeFromPublishedTests) {
        extractTestKnowledgeMutation.mutate({ testId: testDraft?.test.id || '' });
      }
    },
  });

  const addToTestSuite = useTestSuiteDialog({
    onSuiteClick: (testSuiteId) => {
      addToTestSuiteAndPublish({
        testContentId,
        testId: testDraft?.test.id || '',
        testSuiteId,
      });
    },
  });

  // Check if test content contains AI_DYNAMIC steps
  const aiDynamicStepsWithIndex = useMemo(() => {
    return (
      testDraft?.steps
        ?.map((step, index) => ({ step, displayStepNumber: index + 1 }))
        .filter(({ step }) => step.mode === 'AI_DYNAMIC') ?? []
    );
  }, [testDraft?.steps]);

  const hasAiDynamicSteps = aiDynamicStepsWithIndex.length > 0;

  const showAiDynamicWarning = () => {
    modals.openConfirmModal({
      title: (
        <Group gap="xs">
          <IconAlertTriangle
            size={20}
            color="orange"
          />
          <Text fw={600}>Unfinished AI-Generation Found</Text>
        </Group>
      ),
      children: (
        <div className="flex flex-col gap-4">
          <Text>Your test contains steps with unfinished AI generation that need to be handled before publishing.</Text>

          {/* List specific AI-Generation steps */}
          <div>
            <Text
              size="sm"
              fw={500}
              mb="xs"
            >
              Steps that need attention:
            </Text>
            {aiDynamicStepsWithIndex.map(({ step, displayStepNumber }) => {
              return (
                <Text
                  key={step.id}
                  size="sm"
                  c="dimmed"
                  style={{ paddingLeft: '1rem' }}
                >
                  • Step {displayStepNumber}: &ldquo;{step.description.slice(0, 50)}
                  {step.description.length > 50 ? '...' : ''}&rdquo;
                </Text>
              );
            })}
          </div>
          <Text>To publish this test, you need to:</Text>
          <Text
            component="ul"
            style={{ paddingLeft: '1rem' }}
          >
            <li>Finish generating all unfinished steps by previewing the test, OR</li>
            <li>Confirm the partial generation by clicking the &quot;save&quot; button in the unfinished steps</li>
          </Text>
          <Text>
            If you want truly dynamic content inside a step, consider using an <strong>AI Act</strong> action instead.
          </Text>
        </div>
      ),
      labels: { confirm: 'Got it', cancel: '' },
      confirmProps: { color: 'blue' },
      cancelProps: { style: { display: 'none' } },
    });
  };

  const handlePublish = () => {
    if (hasAiDynamicSteps) {
      showAiDynamicWarning();
      return;
    }
    publishTestDraft({ testContentId });
  };

  const handleAddToTestSuite = () => {
    if (hasAiDynamicSteps) {
      showAiDynamicWarning();
      return;
    }
    addToTestSuite();
  };

  return (
    <SplitButton
      size="compact-sm"
      buttonChildren="Publish"
      buttonProps={{
        loading: isPublishing,
        onClick: handlePublish,
      }}
      menuChildren={
        <>
          <Menu.Item
            leftSection={<IconOutbound size={18} />}
            onClick={handlePublish}
            disabled={isPublishing}
          >
            Publish directly
          </Menu.Item>
          <Menu.Item
            leftSection={<IconBoxMultiple size={18} />}
            onClick={handleAddToTestSuite}
          >
            Add to a test suite
          </Menu.Item>
        </>
      }
    />
  );
}
